import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Receipt,
  CreditCard,
  Car,
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  GitBranch,
  Gift,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ModernButton,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Input,
  ConfirmDialog,
} from "../../components/ui";
import { apiService } from "../../services/api";
import type { Sale, Invoice, Payment, SalePayment, SaleBenefit } from "../../types";
import type { ToastType } from "../../components/ui";
import {
  formatCurrency,
  formatDateToBR,
  formatPriceInput,
  parseCurrency,
  numberToCurrencyInputValue,
} from "../../utils/formatters";
import { computeSalePaymentSummary } from "../../utils/salePaymentSummary";
import { PriceInput } from "../../components/form/PriceInput";

type PaymentFormRow = { id: string; name: string };
type PaymentOptionRow = { id: string; name: string; paymentFormId: string };

export type SalePaymentsTabContentProps = {
  sale: Sale;
  onRefresh: () => Promise<void>;
  onToast: (message: string, type: ToastType) => void;
  canEditFinancial: boolean;
};

export const SalePaymentsTabContent: React.FC<SalePaymentsTabContentProps> = ({
  sale,
  onRefresh,
  onToast,
  canEditFinancial,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [forms, setForms] = useState<PaymentFormRow[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newAmount, setNewAmount] = useState("");
  const [newFormId, setNewFormId] = useState("");
  const [newOptionId, setNewOptionId] = useState("");
  const [newPaidAt, setNewPaidAt] = useState("");
  const [newAsPending, setNewAsPending] = useState(false);
  const [addOptions, setAddOptions] = useState<PaymentOptionRow[]>([]);
  const [adding, setAdding] = useState(false);

  const [editOptions, setEditOptions] = useState<PaymentOptionRow[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editFormId, setEditFormId] = useState("");
  const [editOptionId, setEditOptionId] = useState("");
  const [editStatus, setEditStatus] = useState<SalePayment["status"]>("pending");
  const [editPaidAt, setEditPaidAt] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitSourceId, setSplitSourceId] = useState<string | null>(null);
  const [splitA, setSplitA] = useState("");
  const [splitB, setSplitB] = useState("");
  const [splitting, setSplitting] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [cancelSaleOpen, setCancelSaleOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [chargeEditId, setChargeEditId] = useState<string | null>(null);
  const [chargeValue, setChargeValue] = useState("");
  const [chargeDisc, setChargeDisc] = useState("");
  const [savingCharge, setSavingCharge] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await apiService.getPaymentForms();
        setForms(Array.isArray(list) ? list : []);
      } catch {
        setForms([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!newFormId) {
      setAddOptions([]);
      setNewOptionId("");
      return;
    }
    (async () => {
      try {
        const opts = await apiService.getPaymentOptions(newFormId);
        setAddOptions(Array.isArray(opts) ? opts : []);
        setNewOptionId("");
      } catch {
        setAddOptions([]);
      }
    })();
  }, [newFormId]);

  useEffect(() => {
    if (!editingId || !editFormId) {
      if (!editingId) setEditOptions([]);
      return;
    }
    (async () => {
      try {
        const opts = await apiService.getPaymentOptions(editFormId);
        setEditOptions(Array.isArray(opts) ? opts : []);
      } catch {
        setEditOptions([]);
      }
    })();
  }, [editFormId, editingId]);

  const summary = useMemo(() => computeSalePaymentSummary(sale), [sale]);

  const beginEdit = useCallback(
    (p: SalePayment) => {
      setEditingId(p.id);
      setEditAmount(numberToCurrencyInputValue(parseFloat(p.amount.toString())));
      setEditFormId(p.paymentFormId || "");
      setEditOptionId(p.paymentOptionId || "");
      setEditStatus(p.status);
      setEditPaidAt(p.paidAt ? p.paidAt.slice(0, 10) : "");
    },
    []
  );

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditOptions([]);
  }, []);

  const handleSaveEdit = async () => {
    if (!editingId || !sale.id) return;
    const amt = parseCurrency(editAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      onToast(t("financialSales.paymentsTab.invalidAmount"), "error");
      return;
    }
    try {
      setSavingEdit(true);
      await apiService.updateSalePayment(sale.id, editingId, {
        amount: amt,
        paymentFormId: editFormId || null,
        paymentOptionId: editOptionId || null,
        status: editStatus,
        paidAt:
          editStatus === "confirmed"
            ? editPaidAt
              ? new Date(editPaidAt + "T12:00:00").toISOString()
              : new Date().toISOString()
            : null,
      });
      onToast(t("financialSales.paymentsTab.savePaymentSuccess"), "success");
      cancelEdit();
      await onRefresh();
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.savePaymentError"), "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdd = async () => {
    if (!newAmount || !newFormId) return;
    const amt = parseCurrency(newAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      onToast(t("financialSales.paymentsTab.invalidAmount"), "error");
      return;
    }
    try {
      setAdding(true);
      await apiService.addSalePayment(sale.id, {
        amount: amt,
        paymentFormId: newFormId,
        paymentOptionId: newOptionId || undefined,
        paidAt: newAsPending ? undefined : newPaidAt || undefined,
        status: newAsPending ? "pending" : "confirmed",
      });
      onToast(t("financialSales.paymentsTab.addPaymentSuccess"), "success");
      setShowAdd(false);
      setNewAmount("");
      setNewFormId("");
      setNewOptionId("");
      setNewPaidAt("");
      setNewAsPending(false);
      await onRefresh();
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.addPaymentError"), "error");
    } finally {
      setAdding(false);
    }
  };

  const openSplit = (p: SalePayment) => {
    setSplitSourceId(p.id);
    const totalCents = Math.round(parseFloat(p.amount.toString()) * 100);
    const halfCents = Math.floor(totalCents / 2);
    const restCents = totalCents - halfCents;
    setSplitA(formatPriceInput(String(halfCents)));
    setSplitB(formatPriceInput(String(restCents)));
    setSplitOpen(true);
  };

  const handleSplit = async () => {
    if (!splitSourceId || !sale.id) return;
    const a = parseCurrency(splitA);
    const b = parseCurrency(splitB);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) {
      onToast(t("financialSales.paymentsTab.invalidAmount"), "error");
      return;
    }
    try {
      setSplitting(true);
      await apiService.splitSalePayment(sale.id, {
        sourcePaymentId: splitSourceId,
        parts: [
          { amount: a, status: "pending" },
          { amount: b, status: "pending" },
        ],
      });
      onToast(t("financialSales.paymentsTab.splitSuccess"), "success");
      setSplitOpen(false);
      setSplitSourceId(null);
      await onRefresh();
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.splitError"), "error");
    } finally {
      setSplitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId || !sale.id) return;
    try {
      await apiService.deleteSalePayment(sale.id, deleteId);
      onToast(t("financialSales.paymentsTab.deleteSuccess"), "success");
      setDeleteId(null);
      await onRefresh();
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.deleteError"), "error");
    }
  };

  const handleCancelSale = async () => {
    try {
      setCancelling(true);
      await apiService.updateSale(sale.id, { status: "cancelled" });
      onToast(t("financialSales.paymentsTab.cancelSaleSuccess"), "success");
      setCancelSaleOpen(false);
      await onRefresh();
      navigate("/financial/sales");
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.cancelSaleError"), "error");
    } finally {
      setCancelling(false);
    }
  };

  const beginChargeEdit = (c: NonNullable<Sale["charges"]>[0]) => {
    setChargeEditId(c.id);
    setChargeValue(numberToCurrencyInputValue(parseFloat(c.value.toString())));
    setChargeDisc(
      c.discountPercentage != null
        ? parseFloat(String(c.discountPercentage)).toString()
        : ""
    );
  };

  const saveCharge = async () => {
    if (!chargeEditId || !sale.id) return;
    const v = parseCurrency(chargeValue);
    if (!Number.isFinite(v) || v < 0) {
      onToast(t("financialSales.paymentsTab.invalidAmount"), "error");
      return;
    }
    const d =
      chargeDisc.trim() === ""
        ? undefined
        : parseFloat(chargeDisc.replace(",", "."));
    if (d !== undefined && (!Number.isFinite(d) || d < 0 || d > 100)) {
      onToast(t("financialSales.paymentsTab.invalidDiscount"), "error");
      return;
    }
    try {
      setSavingCharge(true);
      await apiService.updateSaleCharge(sale.id, chargeEditId, {
        value: v,
        discountPercentage: chargeDisc.trim() === "" ? null : d,
      });
      onToast(t("financialSales.paymentsTab.chargeSaveSuccess"), "success");
      setChargeEditId(null);
      await onRefresh();
    } catch (e: any) {
      onToast(e?.message || t("financialSales.paymentsTab.chargeSaveError"), "error");
    } finally {
      setSavingCharge(false);
    }
  };

  const methodLabels: Record<string, string> = {
    pix: "Pix",
    cash: "Dinheiro",
    bank_transfer: "Transferência",
    financing: "Financiamento",
    credit_card: "Cartão",
    trade_in: "Troca",
  };

  const salePayStatusStyle: Record<string, string> = {
    confirmed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };

  const proposalPayments = sale.proposal?.payments || [];
  const tradeList = sale.proposal?.tradeVehicles || [];
  const canCancelSale =
    canEditFinancial &&
    sale.status !== "cancelled" &&
    sale.status !== "delivered";

  return (
    <div className="space-y-4 mt-4">
      <Card className="border-accent/20">
        <CardHeader>
          <CardTitle className="text-base">{t("financialSales.paymentsTab.summaryTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">{t("financialSales.paymentsTab.finalValue")}</p>
              <p className="font-semibold">{formatCurrency(summary.finalValue)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("financialSales.paymentsTab.totalConfirmed")}</p>
              <p className="font-semibold text-green-600">{formatCurrency(summary.totalConfirmed)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("financialSales.paymentsTab.totalPending")}</p>
              <p className="font-semibold text-amber-600">{formatCurrency(summary.totalPending)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("financialSales.paymentsTab.remaining")}</p>
              <p className="font-semibold">{formatCurrency(summary.remainingToPay)}</p>
            </div>
          </div>
          {summary.isFullyPaid && (
            <Badge className="mt-3 bg-green-600 text-white">{t("financialSales.paymentsTab.fullyPaidBadge")}</Badge>
          )}
        </CardContent>
      </Card>

      {proposalPayments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {t("financialSales.paymentsTab.proposalReference")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {proposalPayments.map((pp) => (
              <div key={pp.id} className="border border-border rounded-md p-3 space-y-2">
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-medium">
                    {pp.paymentForm?.name}
                    {pp.paymentOption?.name ? ` · ${pp.paymentOption.name}` : ""}
                  </span>
                  <span>{formatCurrency(parseFloat(pp.totalValue))}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("financialSales.paymentsTab.installmentsCount", { count: pp.installments })}
                </p>
                {pp.installmentPayments && pp.installmentPayments.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>{t("financialSales.paymentsTab.dueDate")}</TableHead>
                        <TableHead className="text-right">{t("financialSales.paymentsTab.amount")}</TableHead>
                        <TableHead>{t("financialSales.paymentsTab.status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pp.installmentPayments.map((ins) => (
                        <TableRow key={ins.id}>
                          <TableCell>{ins.installmentNumber}</TableCell>
                          <TableCell>{formatDateToBR(ins.dueDate)}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(parseFloat(ins.value))}
                          </TableCell>
                          <TableCell>{ins.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tradeList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="h-5 w-5" />
              {t("financialSales.paymentsTab.tradeTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/20 rounded-md p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>{t("financialSales.paymentsTab.tradeMismatchHint")}</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("financialSales.paymentsTab.vehicle")}</TableHead>
                  <TableHead className="text-right">{t("financialSales.paymentsTab.offeredValue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tradeList.map((tv) => (
                  <TableRow key={tv.id || `${tv.brandId}-${tv.modelId}`}>
                    <TableCell>
                      {tv.brand?.name} {tv.model?.name} {tv.modelYear}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(
                        typeof tv.offeredValue === "number"
                          ? tv.offeredValue
                          : parseFloat(String(tv.offeredValue))
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {canCancelSale && (
              <ModernButton variant="outline" size="sm" onClick={() => setCancelSaleOpen(true)}>
                {t("financialSales.paymentsTab.cancelSaleCta")}
              </ModernButton>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("financialSales.paymentsTab.chargesTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {sale.charges && sale.charges.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("financialSales.saleChargeNameColumn")}</TableHead>
                  <TableHead className="text-right">{t("financialSales.paymentsTab.chargeValue")}</TableHead>
                  <TableHead className="text-right">{t("financialSales.paymentsTab.discountPct")}</TableHead>
                  {canEditFinancial && <TableHead><span></span></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sale.charges.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.charge?.name || "—"}</TableCell>
                    <TableCell className="text-right">
                      {chargeEditId === c.id ? (
                        <PriceInput
                          value={chargeValue}
                          onChange={setChargeValue}
                          className="w-36 min-w-[9rem] ml-auto"
                        />
                      ) : (
                        formatCurrency(parseFloat(c.value.toString()))
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {chargeEditId === c.id ? (
                        <Input
                          value={chargeDisc}
                          onChange={(e) => setChargeDisc(e.target.value)}
                          className="w-20 ml-auto"
                          placeholder="%"
                        />
                      ) : c.discountPercentage != null ? (
                        `${parseFloat(String(c.discountPercentage)).toFixed(2)}%`
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {canEditFinancial && (
                      <TableCell>
                        {chargeEditId === c.id ? (
                          <div className="flex gap-1">
                            <ModernButton size="sm" onClick={saveCharge} disabled={savingCharge}>
                              {t("actions.save")}
                            </ModernButton>
                            <ModernButton
                              variant="outline"
                              size="sm"
                              onClick={() => setChargeEditId(null)}
                              disabled={savingCharge}
                            >
                              {t("actions.cancel")}
                            </ModernButton>
                          </div>
                        ) : (
                          <ModernButton variant="outline" size="sm" onClick={() => beginChargeEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </ModernButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">{t("financialSales.saleChargesEmpty")}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Gift className="h-5 w-5" />
            {t("financialSales.saleBenefitsTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sale.benefits && sale.benefits.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("financialSales.saleBenefitNameColumn")}</TableHead>
                  <TableHead className="text-right">{t("financialSales.saleBenefitValueColumn")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sale.benefits.map((b: SaleBenefit) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.benefit?.name || "—"}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(parseFloat(b.value.toString()))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">{t("financialSales.saleBenefitsEmpty")}</p>
          )}
        </CardContent>
      </Card>

      {sale.invoices &&
        sale.invoices.some((inv: Invoice) => inv.payments && inv.payments.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                {t("financialSales.paymentsTab.invoicePayments")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sale.invoices.map((invoice: Invoice) => {
                  if (!invoice.payments || invoice.payments.length === 0) return null;
                  return (
                    <div key={invoice.id} className="space-y-2">
                      <div className="flex items-center justify-between pb-2 border-b border-border">
                        <h4 className="font-semibold text-foreground">
                          {t("financialSales.paymentsTab.invoiceNumber", { n: invoice.invoiceNumber })}
                        </h4>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("financialSales.paymentsTab.date")}</TableHead>
                            <TableHead>{t("financialSales.paymentsTab.method")}</TableHead>
                            <TableHead>{t("financialSales.paymentsTab.status")}</TableHead>
                            <TableHead className="text-right">{t("financialSales.paymentsTab.amount")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoice.payments.map((payment: Payment) => (
                            <TableRow key={payment.id}>
                              <TableCell>{formatDateToBR(payment.paymentDate)}</TableCell>
                              <TableCell>{payment.paymentMethod}</TableCell>
                              <TableCell>{payment.status}</TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(parseFloat(payment.amount.toString()))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {t("financialSales.paymentsTab.saleReceipts")}
            </div>
            {canEditFinancial && (
              <ModernButton variant="outline" size="sm" onClick={() => setShowAdd(!showAdd)}>
                <Plus className="h-4 w-4 mr-1" />
                {t("financialSales.paymentsTab.addReceipt")}
              </ModernButton>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {showAdd && canEditFinancial && (
            <div className="mb-4 p-4 border border-border rounded-md space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("financialSales.paymentsTab.amount")} *</Label>
                  <PriceInput
                    className="mt-1"
                    value={newAmount}
                    onChange={setNewAmount}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div>
                  <Label>{t("financialSales.paymentFormLabel")} *</Label>
                  <Select value={newFormId} onValueChange={setNewFormId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={t("financialSales.paymentFormPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {addOptions.length > 0 && (
                  <div>
                    <Label>{t("financialSales.paymentOptionLabel")}</Label>
                    <Select
                      value={newOptionId || "__none__"}
                      onValueChange={(v) => setNewOptionId(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t("financialSales.paymentOptionPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t("financialSales.paymentOptionNone")}</SelectItem>
                        {addOptions.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>{t("financialSales.paymentsTab.paidDate")}</Label>
                  <input
                    type="date"
                    disabled={newAsPending}
                    className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm disabled:opacity-50"
                    value={newPaidAt}
                    onChange={(e) => setNewPaidAt(e.target.value)}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={newAsPending}
                  onChange={(e) => setNewAsPending(e.target.checked)}
                />
                {t("financialSales.paymentsTab.registerAsPending")}
              </label>
              <div className="flex justify-end gap-2">
                <ModernButton variant="outline" size="sm" onClick={() => setShowAdd(false)} disabled={adding}>
                  {t("actions.cancel")}
                </ModernButton>
                <ModernButton size="sm" onClick={handleAdd} disabled={!newAmount || !newFormId || adding}>
                  {adding ? t("actions.loading") : t("actions.save")}
                </ModernButton>
              </div>
            </div>
          )}

          {sale.salePayments && sale.salePayments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("financialSales.paymentsTab.date")}</TableHead>
                  <TableHead>{t("financialSales.paymentFormColumn")}</TableHead>
                  <TableHead>{t("financialSales.paymentsTab.status")}</TableHead>
                  <TableHead className="text-right">{t("financialSales.paymentsTab.amount")}</TableHead>
                  {canEditFinancial && <TableHead><span></span></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sale.salePayments.map((p: SalePayment) => {
                  const formLabel =
                    p.paymentForm?.name ||
                    (p.method ? methodLabels[p.method] || p.method : "—");
                  const optSuffix = p.paymentOption?.name ? ` · ${p.paymentOption.name}` : "";
                  return (
                    <TableRow key={p.id}>
                      {editingId === p.id ? (
                        <>
                          <TableCell>
                            <input
                              type="date"
                              className="w-full px-2 py-1 border border-border rounded text-sm bg-background"
                              value={editPaidAt}
                              onChange={(e) => setEditPaidAt(e.target.value)}
                              disabled={editStatus !== "confirmed"}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="space-y-2 min-w-[140px]">
                              <Select value={editFormId} onValueChange={setEditFormId}>
                                <SelectTrigger className="h-8">
                                  <SelectValue placeholder={t("financialSales.paymentFormPlaceholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {forms.map((f) => (
                                    <SelectItem key={f.id} value={f.id}>
                                      {f.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {editOptions.length > 0 && (
                                <Select
                                  value={editOptionId || "__none__"}
                                  onValueChange={(v) => setEditOptionId(v === "__none__" ? "" : v)}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">{t("financialSales.paymentOptionNone")}</SelectItem>
                                    {editOptions.map((o) => (
                                      <SelectItem key={o.id} value={o.id}>
                                        {o.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={editStatus}
                              onValueChange={(v) => setEditStatus(v as SalePayment["status"])}
                            >
                              <SelectTrigger className="h-8 w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">{t("financialSales.paymentsTab.statusPending")}</SelectItem>
                                <SelectItem value="confirmed">
                                  {t("financialSales.paymentsTab.statusConfirmed")}
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  {t("financialSales.paymentsTab.statusCancelled")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                              className="w-24 ml-auto h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <ModernButton size="sm" onClick={handleSaveEdit} disabled={savingEdit}>
                                {t("actions.save")}
                              </ModernButton>
                              <ModernButton variant="outline" size="sm" onClick={cancelEdit} disabled={savingEdit}>
                                {t("actions.cancel")}
                              </ModernButton>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {p.paidAt ? formatDateToBR(p.paidAt) : formatDateToBR(p.createdAt)}
                          </TableCell>
                          <TableCell>{`${formLabel}${optSuffix}`}</TableCell>
                          <TableCell>
                            <Badge className={salePayStatusStyle[p.status] || ""}>{p.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(parseFloat(p.amount.toString()))}
                          </TableCell>
                          {canEditFinancial && (
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <ModernButton variant="outline" size="sm" onClick={() => beginEdit(p)}>
                                  <Pencil className="h-4 w-4" />
                                </ModernButton>
                                <ModernButton variant="outline" size="sm" onClick={() => openSplit(p)}>
                                  <GitBranch className="h-4 w-4" />
                                </ModernButton>
                                {p.status === "pending" && (
                                  <ModernButton variant="outline" size="sm" onClick={() => setDeleteId(p.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </ModernButton>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            !showAdd && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t("financialSales.paymentsTab.noReceipts")}
              </p>
            )
          )}
        </CardContent>
      </Card>

      {splitOpen && splitSourceId && (
        <Card className="border-accent/30">
          <CardHeader>
            <CardTitle className="text-sm">{t("financialSales.paymentsTab.splitTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("financialSales.paymentsTab.splitDescription")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{t("financialSales.paymentsTab.splitPart1")}</Label>
                <PriceInput value={splitA} onChange={setSplitA} className="mt-1" />
              </div>
              <div>
                <Label>{t("financialSales.paymentsTab.splitPart2")}</Label>
                <PriceInput value={splitB} onChange={setSplitB} className="mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <ModernButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setSplitOpen(false);
                  setSplitSourceId(null);
                }}
                disabled={splitting}
              >
                {t("actions.cancel")}
              </ModernButton>
              <ModernButton size="sm" onClick={handleSplit} disabled={splitting}>
                {splitting ? t("actions.loading") : t("financialSales.paymentsTab.splitConfirm")}
              </ModernButton>
            </div>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        title={t("financialSales.paymentsTab.deleteTitle")}
        description={t("financialSales.paymentsTab.deleteDescription")}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        confirmText={t("actions.confirm")}
        type="warning"
      />

      <ConfirmDialog
        isOpen={cancelSaleOpen}
        title={t("financialSales.paymentsTab.cancelSaleTitle")}
        description={t("financialSales.paymentsTab.cancelSaleDescription")}
        onClose={() => setCancelSaleOpen(false)}
        onConfirm={handleCancelSale}
        confirmText={t("financialSales.paymentsTab.cancelSaleConfirm")}
        isLoading={cancelling}
        type="danger"
      />

      {tradeList.length === 0 && canCancelSale && (
        <Card>
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{t("financialSales.paymentsTab.cancelSaleHint")}</p>
            <ModernButton variant="outline" size="sm" onClick={() => setCancelSaleOpen(true)}>
              {t("financialSales.paymentsTab.cancelSaleCta")}
            </ModernButton>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        <Link to={`/proposals/${sale.proposalId}`} className="text-accent hover:underline">
          {t("financialSales.viewProposal")}
        </Link>
      </p>
    </div>
  );
};
